import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Table from "../../../Components/Table/Table";
import { formatTradePrice } from "../../../helper/formatTradePrice";
import { isoToJalali } from "../../../helper/isoToJalali";
import { API_BASE_URL } from "../../../config/api";

const PARTICIPATION_BASE_PATH = `${API_BASE_URL}/api/v0/payment-payout/admin`;
const NETWORKS_ENDPOINT = `${PARTICIPATION_BASE_PATH}/networks/`;
const INDEPENDENT_BLOGGERS_ENDPOINT = `${PARTICIPATION_BASE_PATH}/bloggers/independent/`;

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function buildIndependentBloggersUrl(page = 1) {
  const params = new URLSearchParams();
  params.set("page", String(page));
  return `${INDEPENDENT_BLOGGERS_ENDPOINT}?${params.toString()}`;
}

function mergeIndependentBloggers(previousBloggers = [], nextBloggers = []) {
  const bloggersMap = new Map();

  previousBloggers.forEach((blogger) => {
    bloggersMap.set(blogger?.user_id ?? blogger?.id ?? blogger?.email, blogger);
  });

  nextBloggers.forEach((blogger) => {
    bloggersMap.set(blogger?.user_id ?? blogger?.id ?? blogger?.email, blogger);
  });

  return Array.from(bloggersMap.values());
}

function formatCount(value) {
  const number = Number(value);
  if (Number.isFinite(number)) return number.toLocaleString();
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
}

function formatPackageBreakdown(value) {
  if (!value) return "-";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.join(" | ") || "-";
  if (typeof value === "object") {
    const parts = Object.entries(value).map(([key, val]) => `${key}: ${val}`);
    return parts.length > 0 ? parts.join(" | ") : "-";
  }
  return String(value);
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return isoToJalali(value);
  } catch {
    return String(value);
  }
}

function StatCard({ title, value }) {
  return (
    <div className="rounded-lg border border-zi-border-light dark:border-zi-border-dark p-3">
      <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60">
        {title}
      </p>
      <p className="text-sm mt-1 text-zi-text-light dark:text-zi-text-dark break-words">
        {value}
      </p>
    </div>
  );
}

export default function InsightsTab({ authFetch }) {
  const [networks, setNetworks] = useState([]);
  const [independentBloggers, setIndependentBloggers] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [independentBloggersLoadingMore, setIndependentBloggersLoadingMore] =
    useState(false);
  const [independentBloggersPagination, setIndependentBloggersPagination] =
    useState({
      page: 1,
      count: 0,
      pageSize: 0,
      totalPages: 1,
    });

  const [selectedNetworkId, setSelectedNetworkId] = useState("");
  const [networkDetail, setNetworkDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [error, setError] = useState("");
  const independentBloggersRequestIdRef = useRef(0);
  const independentBloggersLoadMoreRef = useRef(null);

  const fetchOverviewData = useCallback(async () => {
    setOverviewLoading(true);
    setError("");
    try {
      const [networksResponse, independentResponse] = await Promise.all([
        authFetch(NETWORKS_ENDPOINT, { method: "GET" }),
        authFetch(buildIndependentBloggersUrl(1), { method: "GET" }),
      ]);

      if (!networksResponse || !independentResponse) return;
      if (!networksResponse.ok || !independentResponse.ok) {
        throw new Error("insights-overview-fetch-failed");
      }

      const [networksPayload, independentPayload] = await Promise.all([
        networksResponse.json(),
        independentResponse.json(),
      ]);

      setNetworks(normalizeList(networksPayload));
      const initialIndependentBloggers = normalizeList(independentPayload);
      setIndependentBloggers(initialIndependentBloggers);
      setIndependentBloggersPagination({
        page: independentPayload?.page ?? 1,
        count: independentPayload?.count ?? initialIndependentBloggers.length,
        pageSize:
          independentPayload?.page_size ?? independentPayload?.pageSize ?? 0,
        totalPages:
          independentPayload?.total_pages ??
          independentPayload?.totalPages ??
          1,
      });
    } catch (fetchError) {
      console.error("Error fetching participation insights:", fetchError);
      setError("بارگذاری داده‌های بینش با خطا مواجه شد.");
    } finally {
      setOverviewLoading(false);
    }
  }, [authFetch]);

  const fetchNetworkDetail = useCallback(
    async (networkId) => {
      if (!networkId) {
        setNetworkDetail(null);
        return;
      }

      setDetailLoading(true);
      setError("");

      try {
        const response = await authFetch(`${NETWORKS_ENDPOINT}${networkId}/`, {
          method: "GET",
        });

        if (!response) return;
        if (!response.ok) {
          throw new Error(`insights-detail-fetch-failed-${response.status}`);
        }

        const payload = await response.json();
        setNetworkDetail(payload);
      } catch (fetchError) {
        console.error("Error fetching network insight detail:", fetchError);
        setError("بارگذاری جزئیات شبکه با خطا مواجه شد.");
        setNetworkDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [authFetch],
  );

  const fetchMoreIndependentBloggers = useCallback(async () => {
    if (
      overviewLoading ||
      independentBloggersLoadingMore ||
      independentBloggersPagination.page >=
        independentBloggersPagination.totalPages
    ) {
      return;
    }

    const nextPage = independentBloggersPagination.page + 1;
    const requestId = independentBloggersRequestIdRef.current + 1;
    independentBloggersRequestIdRef.current = requestId;
    setIndependentBloggersLoadingMore(true);

    try {
      const response = await authFetch(buildIndependentBloggersUrl(nextPage), {
        method: "GET",
      });

      if (!response) return;
      if (!response.ok) {
        throw new Error(`independent-bloggers-fetch-failed-${response.status}`);
      }

      const payload = await response.json();
      if (requestId !== independentBloggersRequestIdRef.current) {
        return;
      }

      const nextBloggers = normalizeList(payload);
      setIndependentBloggers((previousBloggers) =>
        mergeIndependentBloggers(previousBloggers, nextBloggers),
      );
      setIndependentBloggersPagination({
        page: payload?.page ?? nextPage,
        count: payload?.count ?? independentBloggersPagination.count,
        pageSize: payload?.page_size ?? independentBloggersPagination.pageSize,
        totalPages:
          payload?.total_pages ??
          payload?.totalPages ??
          independentBloggersPagination.totalPages,
      });
    } catch (fetchError) {
      console.error("Error fetching more independent bloggers:", fetchError);
      setError("بارگذاری داده‌های بینش با خطا مواجه شد.");
    } finally {
      if (requestId === independentBloggersRequestIdRef.current) {
        setIndependentBloggersLoadingMore(false);
      }
    }
  }, [
    authFetch,
    independentBloggersLoadingMore,
    independentBloggersPagination.count,
    independentBloggersPagination.page,
    independentBloggersPagination.pageSize,
    independentBloggersPagination.totalPages,
    overviewLoading,
  ]);

  useEffect(() => {
    fetchOverviewData();
  }, [fetchOverviewData]);

  useEffect(() => {
    if (networks.length === 0) {
      setSelectedNetworkId("");
      setNetworkDetail(null);
      return;
    }

    const hasSelected = networks.some(
      (network) => String(network.id) === String(selectedNetworkId),
    );

    if (!hasSelected) {
      setSelectedNetworkId(String(networks[0].id));
    }
  }, [networks, selectedNetworkId]);

  useEffect(() => {
    fetchNetworkDetail(selectedNetworkId);
  }, [fetchNetworkDetail, selectedNetworkId]);

  useEffect(() => {
    const target = independentBloggersLoadMoreRef.current;

    if (!target) {
      return;
    }

    const scrollContainer = target.closest(".overflow-auto");
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry?.isIntersecting) {
          return;
        }

        fetchMoreIndependentBloggers();
      },
      {
        root: scrollContainer,
        rootMargin: "200px 0px",
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, [fetchMoreIndependentBloggers, independentBloggers.length]);

  const networksTableData = useMemo(() => {
    return networks.map((network) => ({
      idRaw: network.id,
      id: network.id ?? "-",
      name: network.name ?? "-",
      code: network.code ?? "-",
      status: network.is_active ? "فعال" : "غیرفعال",
      owner: network.owner_name ?? "-",
      sale: formatTradePrice(network.entire_sale_usd),
      soldChallenges: formatCount(network.entire_number_of_challenges_sold),
      totalProfit: formatTradePrice(network.total_profit_participants_made),
      joinedUsers: formatCount(network.total_users_joined_via_network),
    }));
  }, [networks]);

  const detailNetwork = networkDetail?.network || null;
  const detailOwner = networkDetail?.owner || null;
  const detailBloggers = Array.isArray(networkDetail?.bloggers)
    ? networkDetail.bloggers
    : [];

  return (
    <div className="w-full min-h-0 flex flex-col gap-2.5">
      <div className="w-full p-5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark border border-zi-border-light dark:border-zi-border-dark flex flex-col gap-4">
        <p className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
          شبکه‌ها (نمای کلی)
        </p>

        {error && <p className="text-red-500 text-sm">{error}</p>}
        {overviewLoading && (
          <p className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
            در حال بارگذاری داده‌ها...
          </p>
        )}

        <Table
          columns={[
            { key: "id", label: "شناسه" },
            { key: "name", label: "نام شبکه" },
            { key: "code", label: "کد" },
            { key: "status", label: "وضعیت" },
            { key: "owner", label: "مالک" },
            { key: "sale", label: "فروش کل" },
            { key: "soldChallenges", label: "تعداد چالش فروخته‌شده" },
            { key: "totalProfit", label: "سود کل مشارکت‌کنندگان" },
            { key: "joinedUsers", label: "کاربران جذب‌شده" },
            {
              key: "detail",
              label: "بینش",
              render: (_, row) => (
                <button
                  type="button"
                  onClick={() => setSelectedNetworkId(String(row.idRaw))}
                  className="rounded-md px-2 py-1 text-xs border border-zi-blue-light text-zi-blue-light"
                >
                  نمایش جزئیات
                </button>
              ),
            },
          ]}
          data={networksTableData}
        />
      </div>

      <div className="w-full p-5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark border border-zi-border-light dark:border-zi-border-dark flex flex-col gap-4">
        <p className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
          بینش شبکه انتخاب‌شده
        </p>

        {detailLoading && (
          <p className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
            در حال بارگذاری جزئیات شبکه...
          </p>
        )}

        {!detailLoading && !detailNetwork && (
          <p className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
            برای مشاهده بینش، یک شبکه را از جدول بالا انتخاب کنید.
          </p>
        )}

        {!detailLoading && detailNetwork && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2.5">
              <StatCard
                title="شبکه"
                value={`${detailNetwork.name || "-"} (${detailNetwork.code || "-"})`}
              />
              <StatCard
                title="وضعیت"
                value={detailNetwork.is_active ? "فعال" : "غیرفعال"}
              />
              <StatCard
                title="فروش کل"
                value={formatTradePrice(detailNetwork.entire_sale_usd)}
              />
              <StatCard
                title="کل چالش فروخته‌شده"
                value={formatCount(
                  detailNetwork.entire_number_of_challenges_sold,
                )}
              />
              <StatCard
                title="فروش به تفکیک پکیج"
                value={formatPackageBreakdown(
                  detailNetwork.entire_number_of_challenges_by_package,
                )}
              />
              <StatCard
                title="سود کل مشارکت‌کنندگان"
                value={formatTradePrice(
                  detailNetwork.total_profit_participants_made,
                )}
              />
              <StatCard
                title="کل پرداخت‌شده به مشارکت‌کنندگان"
                value={formatTradePrice(
                  detailNetwork.total_participants_paid_out,
                )}
              />
              <StatCard
                title="کل قابل برداشت مشارکت‌کنندگان"
                value={formatTradePrice(
                  detailNetwork.total_participants_can_payout,
                )}
              />
              <StatCard
                title="کاربران جذب‌شده از شبکه"
                value={formatCount(
                  detailNetwork.total_users_joined_via_network,
                )}
              />
            </div>

            {detailOwner && (
              <>
                <p className="text-sm font-medium text-zi-text-light dark:text-zi-text-dark mt-2">
                  بینش مالک شبکه
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2.5">
                  <StatCard title="نام" value={detailOwner.name || "-"} />
                  <StatCard title="ایمیل" value={detailOwner.email || "-"} />
                  <StatCard
                    title="فروش مستقیم"
                    value={formatTradePrice(detailOwner.direct_sales_usd)}
                  />
                  <StatCard
                    title="تعداد چالش مستقیم"
                    value={formatCount(detailOwner.direct_number_of_challenges)}
                  />
                  <StatCard
                    title="چالش مستقیم به تفکیک پکیج"
                    value={formatPackageBreakdown(
                      detailOwner.direct_number_of_challenges_by_package,
                    )}
                  />
                  <StatCard
                    title="تعداد چالش غیرمستقیم"
                    value={formatCount(
                      detailOwner.indirect_number_of_challenges,
                    )}
                  />
                  <StatCard
                    title="چالش غیرمستقیم به تفکیک پکیج"
                    value={formatPackageBreakdown(
                      detailOwner.indirect_number_of_challenges_by_package,
                    )}
                  />
                  <StatCard
                    title="سود مستقیم"
                    value={formatTradePrice(detailOwner.entire_profit_direct)}
                  />
                  <StatCard
                    title="سود غیرمستقیم"
                    value={formatTradePrice(detailOwner.entire_profit_indirect)}
                  />
                  <StatCard
                    title="سود کل"
                    value={formatTradePrice(detailOwner.entire_profit_total)}
                  />
                  <StatCard
                    title="سود پرداخت‌شده"
                    value={formatTradePrice(
                      detailOwner.entire_profit_payed_out,
                    )}
                  />
                  <StatCard
                    title="سود قابل برداشت"
                    value={formatTradePrice(
                      detailOwner.entire_profit_can_payout,
                    )}
                  />
                  <StatCard
                    title="کاربران جذب‌شده مستقیم"
                    value={formatCount(
                      detailOwner.entire_users_joined_directly,
                    )}
                  />
                </div>
              </>
            )}

            <p className="text-sm font-medium text-zi-text-light dark:text-zi-text-dark mt-2">
              بلاگرهای شبکه
            </p>
            <Table
              columns={[
                { key: "userId", label: "شناسه کاربر" },
                { key: "name", label: "نام" },
                { key: "email", label: "ایمیل" },
                { key: "membershipId", label: "شناسه عضویت" },
                { key: "membershipStarted", label: "شروع عضویت" },
                { key: "membershipEnded", label: "پایان عضویت" },
                { key: "sold", label: "تعداد فروش" },
                { key: "soldByPackage", label: "فروش به تفکیک پکیج" },
                { key: "profitMade", label: "سود ساخته‌شده" },
                { key: "profitPaid", label: "سود پرداخت‌شده" },
                { key: "profitCanPayout", label: "سود قابل برداشت" },
              ]}
              data={detailBloggers.map((blogger) => ({
                userId: blogger.user_id ?? "-",
                name: blogger.name ?? "-",
                email: blogger.email ?? "-",
                membershipId: blogger.membership_id ?? "-",
                membershipStarted: formatDate(blogger.membership_started_at),
                membershipEnded: formatDate(blogger.membership_ended_at),
                sold: formatCount(blogger.entire_challenges_count_sold),
                soldByPackage: formatPackageBreakdown(
                  blogger.entire_challenges_count_by_package,
                ),
                profitMade: formatTradePrice(blogger.profit_made),
                profitPaid: formatTradePrice(blogger.profit_payed_out),
                profitCanPayout: formatTradePrice(blogger.profit_can_payout),
              }))}
            />
          </>
        )}
      </div>

      <div className="w-full p-5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark border border-zi-border-light dark:border-zi-border-dark flex flex-col gap-4">
        <p className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
          لیست بلاگرهای مستقل
        </p>

        <Table
          columns={[
            { key: "userId", label: "شناسه کاربر" },
            { key: "name", label: "نام" },
            { key: "email", label: "ایمیل" },
            { key: "sold", label: "تعداد فروش" },
            { key: "soldByPackage", label: "فروش به تفکیک پکیج" },
            { key: "profitMade", label: "سود ساخته‌شده" },
            { key: "profitPaid", label: "سود پرداخت‌شده" },
            { key: "profitCanPayout", label: "سود قابل برداشت" },
          ]}
          data={independentBloggers.map((blogger) => ({
            userId: blogger.user_id ?? "-",
            name: blogger.name ?? "-",
            email: blogger.email ?? "-",
            sold: formatCount(blogger.entire_challenges_count_sold),
            soldByPackage: formatPackageBreakdown(
              blogger.entire_challenges_count_by_package,
            ),
            profitMade: formatTradePrice(blogger.profit_made),
            profitPaid: formatTradePrice(blogger.profit_payed_out),
            profitCanPayout: formatTradePrice(blogger.profit_can_payout),
          }))}
        />
        <div
          ref={independentBloggersLoadMoreRef}
          className="w-full py-3 text-center text-sm text-zi-text-light/60 dark:text-zi-text-dark/60"
        >
          {independentBloggersLoadingMore ? "در حال بارگذاری..." : ""}
        </div>
      </div>
    </div>
  );
}
